import axios, { AxiosRequestConfig } from 'axios';

// 通用接口定义
export interface AIModelResponse {
      content: string;
}

export interface StreamCallbacks {
  onData: (data: string) => void;
  onProgress: (progress: number) => void;
  onComplete: (fullResponse?: string) => void;
  onError: (error: any) => void;
}

// 基础AI模型适配器接口
export interface AIModelAdapter {
  generateResponse(prompt: string, systemPrompt: string, callbacks?: StreamCallbacks): Promise<AIModelResponse>;
  getName(): string;
  getConfigFields(): Array<{key: string, label: string, type: 'text' | 'password' | 'select', options?: {value: string, label: string}[]}>;
}

// OpenAI适配器实现
export class OpenAIAdapter implements AIModelAdapter {
  private apiKey: string;
  private baseUrl: string;
  private model: string;

  constructor(config: {apiKey: string, baseUrl?: string, model?: string}) {
    this.apiKey = config.apiKey;
    this.baseUrl = config.baseUrl || 'https://api.openai.com/v1/chat/completions';
    this.model = config.model || 'gpt-3.5-turbo';
  }

  getName(): string {
    return 'OpenAI';
  }

  getConfigFields() {
    return [
      {key: 'apiKey', label: 'API密钥', type: 'password' as const},
      {key: 'baseUrl', label: 'API基础URL', type: 'text' as const},
      {key: 'model', label: '模型', type: 'select' as const, options: [
        {value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo'},
        {value: 'gpt-4', label: 'GPT-4'},
        {value: 'gpt-4-turbo', label: 'GPT-4 Turbo'},
      ]},
    ];
  }

  async generateResponse(
    prompt: string,
    systemPrompt: string,
    callbacks?: StreamCallbacks
  ): Promise<AIModelResponse> {
    try {
      const requestConfig: AxiosRequestConfig = {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        responseType: callbacks ? 'text' : 'json',
        onDownloadProgress: callbacks ? (progressEvent) => {
          if (progressEvent.event && progressEvent.event.target) {
            const target = progressEvent.event.target as XMLHttpRequest;
            const content = target.responseText;
            if (content) {
              this.processStreamData(content, callbacks);
            }
          }
          
          if (progressEvent.loaded && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            callbacks.onProgress(progress);
          }
        } : undefined
      };

      const response = await axios.post(
        this.baseUrl,
        {
          model: this.model,
          messages: [
            {
              role: 'system',
              content: systemPrompt
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          stream: !!callbacks
        },
        requestConfig
      );

      if (callbacks) {
        let fullResponse = this.processStreamResponse(response.data, callbacks);
        return { content: fullResponse };
      }

      return { content: response.data.choices[0]?.message?.content || '' };
    } catch (error) {
      this.handleError(error, callbacks);
      throw error;
    }
  }

  private processStreamResponse(responseData: string, callbacks: StreamCallbacks): string {
          let fullResponse = '';
    
    if (typeof responseData === 'string') {
      const lines = responseData.split('\n').filter(line => line.trim() !== '');
          
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.substring(6);
              
              if (data === '[DONE]') {
                continue;
              }
              
              try {
                const parsed = JSON.parse(data);
                if (parsed.choices && parsed.choices[0] && parsed.choices[0].delta && parsed.choices[0].delta.content) {
                  const content = parsed.choices[0].delta.content;
                  fullResponse += content;
                }
              } catch (e) {
                console.error('解析流式数据出错:', e);
              }
            }
          }
          
      callbacks.onComplete(fullResponse);
    }
    
    return fullResponse;
  }

  private processStreamData(content: string, callbacks: StreamCallbacks): void {
    const lines = content.split('\n');
    for (const line of lines) {
      if (line.startsWith('data: ')) {
        try {
          const jsonData = JSON.parse(line.slice(6));
          if (jsonData.choices && jsonData.choices[0] && jsonData.choices[0].delta && jsonData.choices[0].delta.content) {
            callbacks.onData(jsonData.choices[0].delta.content);
          }
        } catch (e) {
          // 忽略解析错误，可能是不完整的JSON
        }
      }
    }
  }

  private handleError(error: any, callbacks?: StreamCallbacks): void {
    const errorMessage = this.getErrorMessage(error);
    if (callbacks) {
      callbacks.onError(errorMessage);
    }
    console.error('OpenAI API请求出错:', errorMessage);
  }

  private getErrorMessage(error: any): string {
    if (error.response) {
      // 服务器返回了非2xx响应
      const status = error.response.status;
      const data = error.response.data;
      
      if (status === 401) {
        return 'API密钥验证失败，请检查您的OpenAI API密钥是否正确';
      } else if (status === 429) {
        return '请求超出了速率限制，请稍后再试或检查您的API使用限额';
      } else if (status === 500) {
        return 'OpenAI服务器错误，请稍后再试';
      }
      
      return `API错误 (${status}): ${data?.error?.message || JSON.stringify(data) || '未知错误'}`;
    } else if (error.request) {
      // 请求已发送但没有收到响应
      return '无法连接到OpenAI服务器，请检查您的网络连接';
    }
    
    // 其他错误
    return error.message || '发生未知错误';
        }
}

// Claude适配器实现
export class ClaudeAdapter implements AIModelAdapter {
  private apiKey: string;
  private baseUrl: string;
  private model: string;

  constructor(config: {apiKey: string, baseUrl?: string, model?: string}) {
    this.apiKey = config.apiKey;
    this.baseUrl = config.baseUrl || 'https://api.anthropic.com/v1/messages';
    this.model = config.model || 'claude-3-opus-20240229';
  }

  getName(): string {
    return 'Claude';
  }

  getConfigFields() {
    return [
      {key: 'apiKey', label: 'API密钥', type: 'password' as const},
      {key: 'baseUrl', label: 'API基础URL', type: 'text' as const},
      {key: 'model', label: '模型', type: 'select' as const, options: [
        {value: 'claude-3-opus-20240229', label: 'Claude 3 Opus'},
        {value: 'claude-3-sonnet-20240229', label: 'Claude 3 Sonnet'},
        {value: 'claude-3-haiku-20240307', label: 'Claude 3 Haiku'},
        {value: 'claude-2.1', label: 'Claude 2.1'},
      ]},
    ];
  }

  async generateResponse(
    prompt: string,
    systemPrompt: string,
    callbacks?: StreamCallbacks
  ): Promise<AIModelResponse> {
    try {
      const requestConfig: AxiosRequestConfig = {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': this.apiKey,
          'anthropic-version': '2023-06-01'
        },
        responseType: callbacks ? 'text' : 'json',
        onDownloadProgress: callbacks ? (progressEvent) => {
          if (progressEvent.event && progressEvent.event.target) {
            const target = progressEvent.event.target as XMLHttpRequest;
            const content = target.responseText;
            if (content) {
              this.processStreamData(content, callbacks);
            }
          }
          
          if (progressEvent.loaded && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            callbacks.onProgress(progress);
          }
        } : undefined
      };

      const response = await axios.post(
        this.baseUrl,
        {
          model: this.model,
          system: systemPrompt,
          messages: [
            {
              role: 'user',
              content: prompt
            }
          ],
          stream: !!callbacks
        },
        requestConfig
      );

      if (callbacks) {
        let fullResponse = this.processStreamResponse(response.data, callbacks);
        return { content: fullResponse };
      }

      return { content: response.data.content[0]?.text || '' };
    } catch (error) {
      this.handleError(error, callbacks);
      throw error;
    }
  }

  private processStreamResponse(responseData: string, callbacks: StreamCallbacks): string {
    let fullResponse = '';
    
    if (typeof responseData === 'string') {
      const lines = responseData.split('\n').filter(line => line.trim() !== '');
      
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.substring(6);
          
          if (data === '[DONE]') {
            continue;
          }
          
          try {
            const parsed = JSON.parse(data);
            if (parsed.type === 'content_block_delta' && parsed.delta && parsed.delta.text) {
              const content = parsed.delta.text;
              fullResponse += content;
            }
          } catch (e) {
            console.error('解析Claude流式数据出错:', e);
          }
        }
      }
      
      callbacks.onComplete(fullResponse);
    }
    
    return fullResponse;
  }

  private processStreamData(content: string, callbacks: StreamCallbacks): void {
    const lines = content.split('\n');
    for (const line of lines) {
      if (line.startsWith('data: ')) {
        try {
          const jsonData = JSON.parse(line.slice(6));
          if (jsonData.type === 'content_block_delta' && jsonData.delta && jsonData.delta.text) {
            callbacks.onData(jsonData.delta.text);
          }
        } catch (e) {
          // 忽略解析错误，可能是不完整的JSON
        }
      }
    }
  }

  private handleError(error: any, callbacks?: StreamCallbacks): void {
    const errorMessage = this.getErrorMessage(error);
    if (callbacks) {
      callbacks.onError(errorMessage);
    }
    console.error('Claude API请求出错:', errorMessage);
  }

  private getErrorMessage(error: any): string {
    if (error.response) {
      const status = error.response.status;
      const data = error.response.data;
      
      if (status === 401) {
        return 'API密钥验证失败，请检查您的Claude API密钥是否正确';
      } else if (status === 429) {
        return '请求超出了速率限制，请稍后再试或检查您的API使用限额';
      } else if (status === 500) {
        return 'Claude服务器错误，请稍后再试';
      }
      
      return `API错误 (${status}): ${data?.error?.message || JSON.stringify(data) || '未知错误'}`;
    } else if (error.request) {
      return '无法连接到Claude服务器，请检查您的网络连接';
        }
    
    return error.message || '发生未知错误';
  }
}

// API服务工厂类
export class AIModelFactory {
  static createAdapter(type: string, config: any): AIModelAdapter {
    switch (type.toLowerCase()) {
      case 'openai':
        return new OpenAIAdapter(config);
      case 'claude':
        return new ClaudeAdapter(config);
      default:
        throw new Error(`不支持的AI模型类型: ${type}`);
    }
  }
  
  static getAvailableModels() {
    return [
      { id: 'openai', name: 'OpenAI (GPT)' },
      { id: 'claude', name: 'Anthropic (Claude)' }
    ];
  }
}

// 主API服务类
export default class AIService {
  private adapter: AIModelAdapter;
  private retryCount: number = 0;
  private maxRetries: number = 2;
  private retryDelay: number = 1000; // ms

  constructor(adapterType: string, config: any) {
    this.adapter = AIModelFactory.createAdapter(adapterType, config);
  }
  
  async generateDecisionTree(
    question: string,
    systemPrompt: string,
    callbacks?: StreamCallbacks
  ): Promise<AIModelResponse> {
    try {
      return await this.adapter.generateResponse(question, systemPrompt, callbacks);
    } catch (error) {
      return await this.handleErrorWithRetry(question, systemPrompt, callbacks, error);
    }
  }
  
  private async handleErrorWithRetry(
    question: string, 
    systemPrompt: string, 
    callbacks?: StreamCallbacks,
    error?: any
  ): Promise<AIModelResponse> {
    if (this.retryCount < this.maxRetries) {
      this.retryCount++;
      const delay = this.retryDelay * this.retryCount;
      
      console.log(`尝试重新连接 (${this.retryCount}/${this.maxRetries})，等待 ${delay}ms...`);
      callbacks?.onData(`\n\n[系统: 连接中断，正在尝试重新连接 (${this.retryCount}/${this.maxRetries})...]\n\n`);
      
      // 等待延迟
      await new Promise(resolve => setTimeout(resolve, delay));
      
      return this.generateDecisionTree(question, systemPrompt, callbacks);
    }
    
    // 重置重试计数
    this.retryCount = 0;
    
    // 如果已达到最大重试次数，则抛出错误
    throw error || new Error('达到最大重试次数，请求失败');
  }
} 